import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { InterfaceEmployee } from 'app/models/employee.model';
import { InterfaceIndicator } from 'app/models/indicator.model';
import { timezone } from 'assets/constants/timezone';
import { take } from 'rxjs/operators';
import { IndicatorService } from 'app/services/indicator.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { EmployeeService } from 'app/services/employee.service';
import * as dayjs from 'dayjs';
import * as utc from 'dayjs/plugin/utc';
import * as timezoneDayJs from 'dayjs/plugin/timezone';
import * as isoWeek from 'dayjs/plugin/isoWeek';
import * as dayOfYear from 'dayjs/plugin/dayOfYear';
dayjs.extend(utc);
dayjs.extend(timezoneDayJs);
dayjs.extend(isoWeek);
dayjs.extend(dayOfYear);

@Component({
  selector: 'app-my-status',
  templateUrl: './my-status.component.html',
  styleUrls: ['./my-status.component.scss']
})
export class MyStatusComponent implements OnInit {

  @Input() user: InterfaceEmployee;
  @Output() emitWeekOrMonth = new EventEmitter<any>(false);
  indicators: InterfaceIndicator[] = [];
  mesurements: any = {};
  isLoading = true;

  // indicators data
  startPeriodUnix: number;
  endPeriodUnix: number;
  currentWeek: number;
  currentYear: number;
  weekNumber: number;
  todayDate: string;
  togglePeriod = true;
  currentMonth: number;
  startPeriod: string;
  endPeriod: string;

  constructor(
    protected indicatorService: IndicatorService,
    private spinner: NgxSpinnerService,
    protected employeeService: EmployeeService
  ) {
    this.weekNumber = dayjs().tz(timezone).isoWeek();
    this.startPeriodUnix = dayjs().tz(timezone).startOf("isoWeek").unix();
    this.endPeriodUnix = dayjs().tz(timezone).endOf("isoWeek").unix();
    this.currentWeek = dayjs().tz(timezone).isoWeek();
    this.currentYear = dayjs().tz(timezone).year();
    this.todayDate = dayjs().tz(timezone).format("DD/MM/YYYY");
    this.currentMonth = Number(dayjs().tz(timezone).format("MM"));
    this.startPeriod = dayjs().tz(timezone).startOf("isoWeek").format("DD/MM/YYYY");
    this.endPeriod = dayjs().tz(timezone).endOf("isoWeek").format("DD/MM/YYYY");
  }

  async ngOnInit(): Promise<void> {
    this.user = await this.employeeService.getCurrentUser();
    this.getIndicators();
  }

  getIndicators(): void {
    this.spinner.show(undefined,
      {
        type: 'square-jelly-box',
        size: 'medium',
        bdColor: 'rgba(0, 0, 0, 0.8)',
        color: '#fff',
        fullScreen: true
      });
    this.indicators = [];
    const body = {
      indicatorsIds: this.user.indicators,
      startPeriodUnix: this.startPeriodUnix,
      endPeriodUnix: this.endPeriodUnix,
      filterBy: {
        weekOrMonth: this.togglePeriod ? "weekNumber" : "monthNumber",
        yearNumber: this.currentYear,
        numberWeekOrMonth: this.togglePeriod ? this.currentWeek : this.currentMonth,
      },
      period: 4
    };
    this.indicatorService.getActiveIndicators(body).pipe(take(1)).subscribe((result: any) => {
      if (result && result.body) {
        for (let i = 0; i < result.body.indicators.length; i++) {
          for (let j = 0; j < result.body.indicators[i].length; j++) {
            for (let k = 0; k < result.body.indicators[i][j].length; k++) {
              this.indicators.push(result.body.indicators[i][j][k]);
            }
          }
        }
        this.mesurements = result.body.mesurements;
        this.isLoading = false;
        this.spinner.hide();
      }
    }, (error: any) => {
      console.error(error);
      this.spinner.hide();
    });
  }

  // switchs the toggle button
  switchDisplayIndicators(): void {
    this.togglePeriod = !this.togglePeriod;
    if (this.togglePeriod) {
      this.getCurrentWeek();
    } else {
      this.getCurrentMonth();
    }
  }

  // returns the amount gived in the format of the indicator type
  // 1 = currency, 2 = percentage, 3 = plain number
  getFormat(indicatorType: number, amount: number): string {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
      minimumFractionDigits: 0
    });
    switch (indicatorType) {
      case 1:
        if (amount) {
          return formatter.format(amount);
        } else {
          return formatter.format(0);
        }
      case 2:
        if (amount) {
          return amount + "%"
        } else {
          return "0%"
        }
      case 3:
        if (amount) {
          return amount.toString()
        } else {
          return "0";
        }
      default:
        return "";
    }
  }

  // return month name
  returnMonthFormat(monthNumber: number): string {
    switch (monthNumber) {
      case 1:
        return "Enero";
      case 2:
        return "Febrero";
      case 3:
        return "Marzo";
      case 4:
        return "Abril";
      case 5:
        return "Mayo";
      case 6:
        return "Junio";
      case 7:
        return "Julio";
      case 8:
        return "Agosto";
      case 9:
        return "Septiembre";
      case 10:
        return "Octubre";
      case 11:
        return "Noviembre";
      case 12:
        return "Diciembre";
      default:
        return "Error";
    }
  }

  // get the number of the previus week
  getLastWeek(): void {
    if (this.currentWeek === 1) {
      this.currentWeek = 52;
      this.currentYear--;
    } else {
      this.currentWeek--;
    }
    this.setValuesWeek();
    this.getIndicators();
  }

  // get the current Week and year
  getCurrentWeek(): void {
    this.currentWeek = dayjs().tz(timezone).isoWeek();
    this.currentYear = dayjs().tz(timezone).year();
    this.startPeriodUnix = dayjs().tz(timezone).startOf("isoWeek").unix();
    this.endPeriodUnix = dayjs().tz(timezone).endOf("isoWeek").unix();
    this.startPeriod = dayjs().tz(timezone).startOf("isoWeek").format("DD/MM/YYYY");
    this.endPeriod = dayjs().tz(timezone).endOf("isoWeek").format("DD/MM/YYYY");
    this.getIndicators();
  }

  // get the number of the next week
  getNextWeek(): void {
    if (this.currentWeek === 52) {
      this.currentWeek = 1;
      this.currentYear++;
    } else {
      this.currentWeek++;
    }
    this.setValuesWeek();
    this.getIndicators();
  }

  // set the values of the week
  setValuesWeek(): void {
    this.startPeriod = dayjs().year(this.currentYear).startOf('year').add(this.currentWeek, "weeks").startOf("isoWeek").format("DD/MM/YYYY");
    this.endPeriod = dayjs().year(this.currentYear).startOf('year').add(this.currentWeek, "weeks").endOf("isoWeek").format("DD/MM/YYYY");
    this.startPeriodUnix = dayjs().year(this.currentYear).startOf('year').add(this.currentWeek, "weeks").startOf("isoWeek").unix();
    this.endPeriodUnix = dayjs().year(this.currentYear).startOf('year').add(this.currentWeek, "weeks").endOf("isoWeek").unix();
  }

  // set the values of the month
  setValuesMonth(): void {
    this.startPeriod = dayjs().year(this.currentYear).startOf('year').add(this.currentMonth, "months").startOf("month").format("DD/MM/YYYY");
    this.endPeriod = dayjs().year(this.currentYear).startOf('year').add(this.currentMonth, "months").endOf("month").format("DD/MM/YYYY");
    this.startPeriodUnix = dayjs().year(this.currentYear).startOf('year').add(this.currentMonth, "months").startOf("month").unix();
    this.endPeriodUnix = dayjs().year(this.currentYear).startOf('year').add(this.currentMonth, "months").endOf("month").unix();
  }

  // get the last month data
  getLastMonth(): void {
    if (this.currentMonth === 1) {
      this.currentMonth = 12;
      this.currentYear--;
    } else {
      this.currentMonth--;
    }
    this.setValuesMonth();
    this.getIndicators();
  }

  // get the current month data
  getCurrentMonth(): void {
    this.currentYear = dayjs().tz(timezone).year();
    this.startPeriodUnix = dayjs().tz(timezone).startOf("isoWeek").unix();
    this.endPeriodUnix = dayjs().tz(timezone).endOf("isoWeek").unix();
    this.startPeriod = dayjs().tz(timezone).startOf("isoWeek").format("DD/MM/YYYY");
    this.endPeriod = dayjs().tz(timezone).endOf("isoWeek").format("DD/MM/YYYY");
    this.getIndicators();
  }

  // get the next month data
  getNextMonth(): void {
    if (this.currentMonth === 12) {
      this.currentMonth = 1;
      this.currentYear++;
    } else {
      this.currentMonth++;
    }
    this.setValuesMonth();
    this.getIndicators();
  }

}
